import { simpleStringHash } from '@eventbrite/string-utils';
import has from 'lodash/has';
import omit from 'lodash/omit';
import some from 'lodash/some';
import values from 'lodash/values';

import { ENTITY_TYPE_MAP } from '../../../constants';
import {
    CITY_BROWSE_SECTION,
    ONLINE_BROWSE_SECTION,
} from '../../../constants/analytics';
import { TLD_UNITED_STATES } from '../../../constants/constants';
import {
    BrandState,
    BucketConfig,
    entities,
    LocationState,
    TLD,
} from '../../../types/index';
import { CARD_INFO_LOOKUP_MAP } from '../../../utils/general';
import {
    BROWSE_HEADER_PLACE_OVERRIDES,
    BROWSE_HEADER_TLD_OVERRIDES,
} from '../constants';

/**
 * Should hide mask
 * @returns true if the mask should be hidden, false if the mask should be visible
 */
export const getMaskState = (
    {
        headerImageUrl,
        ...pageBrandInfo
    }: { headerImageUrl?: string; pageBrandInfo?: any },
    isOnline?: boolean,
) => {
    /* If we have no image at all, always show the gradient & mask */
    if (!headerImageUrl) {
        return false;
    }

    /* We always want to hide the mask for online */
    if (isOnline) {
        return true;
    }
    /* If any brand content has a truthy value (besides headerImageUrl
    and headerSmallImageUrl) hide the mask. */
    let result: any = omit(pageBrandInfo, [
        'headerImageUrl',
        'headerSmallImageUrl',
    ]);

    result = values(result);
    result = some(result);

    return result;
};

/**
 * Overrides the brand state with any localized
 *   Initially used to support an english-only marketing campaign
 * @param brand The server-provided brand state
 * @param location The location for this user
 * @returns An updated brand state with localized overrides
 */
export const getLocalizedBrandOverrides = (
    brand: BrandState,
    location: LocationState,
    tld: TLD = TLD_UNITED_STATES,
) => {
    let brandOverrides = {};
    if (
        location.placeId &&
        Object.keys(BROWSE_HEADER_PLACE_OVERRIDES).includes(location.placeId)
    ) {
        // Override brand by placeId
        brandOverrides = BROWSE_HEADER_PLACE_OVERRIDES[location.placeId];
    } else if (isInTLDOverrides(tld)) {
        // Generic override by TLD
        brandOverrides = BROWSE_HEADER_TLD_OVERRIDES[tld];
    }
    return {
        ...brand,
        ...brandOverrides,
    };
};

/**
 * Type guard for refining TLD values
 * @param tld The user's TLD
 * @returns If tld has a header override
 */
function isInTLDOverrides(
    tld: TLD,
): tld is keyof typeof BROWSE_HEADER_TLD_OVERRIDES {
    return Object.keys(BROWSE_HEADER_TLD_OVERRIDES).includes(tld);
}

export const getRandomAsset = (assetList: string[], hashTarget: string) =>
    //HACK Set as an explicit any as `simpleStringHash` returns a string, but has a mathematical operation applied to it
    assetList[(simpleStringHash(hashTarget) as any) % assetList.length];

export const getBucketContent = (
    { entities }: { entities: entities },
    bucketConfig: BucketConfig,
    currencyFormat: string,
    section: string,
) => {
    const { type, ...remainingBucketConfig } = bucketConfig;

    let content;

    if (has(ENTITY_TYPE_MAP, type)) {
        const entityKey = ENTITY_TYPE_MAP[type as string];

        if (remainingBucketConfig[entityKey]) {
            const bucketContent = remainingBucketConfig[entityKey].map(
                (id: string) => entities[id],
            );

            content = CARD_INFO_LOOKUP_MAP[type as string]({
                bucketsContent: bucketContent,
                currencyFormat,
                section,
            });
        }
    }

    return content;
};

export const getBrowseSection = (isOnlineBrowse: boolean) =>
    isOnlineBrowse ? ONLINE_BROWSE_SECTION : CITY_BROWSE_SECTION;
